import React, { useEffect, useMemo, useRef, useState } from "react";
import { ICON_MAP } from "./BuilderSections";
import { StarIcon } from "@heroicons/react/24/outline";

type Props = {
    value: string;
    color?: string;
    onChange: (icon: string) => void;
    onColorChange?: (color: string) => void;
};

const IconPicker: React.FC<Props> = ({ value, color = "#ffffff", onChange, onColorChange }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [tooltip, setTooltip] = useState<string | null>(null);
    const ref = useRef<HTMLDivElement>(null);
    const popRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    // Compute inside component so ICON_MAP is guaranteed populated
    const allIcons = useMemo(
        () => Object.keys(ICON_MAP).map((key) => ({
            value: key,
            label: key.startsWith("l-") ? key.slice(2) : key,
        })),
        []
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? allIcons.filter((o) => o.label.includes(q)) : allIcons;
    }, [search, allIcons]);

    useEffect(() => {
        if (!open) { setSearch(""); return; }
        setTimeout(() => searchRef.current?.focus(), 50);
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    useEffect(() => {
        if (!open || !popRef.current || !ref.current) return;
        const trigger = ref.current.getBoundingClientRect();
        const pop = popRef.current;
        const popH = 420;
        const popW = 360;
        let top = trigger.bottom + 8;
        let left = trigger.left;
        if (top + popH > window.innerHeight) top = trigger.top - popH - 8;
        if (left + popW > window.innerWidth) left = window.innerWidth - popW - 8;
        pop.style.top = `${Math.max(4, top)}px`;
        pop.style.left = `${Math.max(4, left)}px`;
    }, [open]);

    const CurrentIcon = ICON_MAP[value] || StarIcon;
    const displayLabel = value.startsWith("l-") ? value.slice(2) : value;

    return (
        <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
            {/* Trigger */}
            <button
                type="button"
                onClick={() => setOpen((p) => !p)}
                title="Pick icon"
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "5px 10px",
                    borderRadius: "6px",
                    border: open ? "1.5px solid #3b82f6" : "1.5px solid #4b5563",
                    background: open ? "#1e3a5f" : "#374151",
                    color: "#f9fafb",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    transition: "all 0.15s",
                    minWidth: 110,
                }}
            >
                <span
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 22,
                        height: 22,
                        borderRadius: 4,
                        background: "rgba(255,255,255,0.08)",
                        flexShrink: 0,
                    }}
                >
                    <CurrentIcon style={{ width: 15, height: 15, color }} />
                </span>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", textAlign: "left" }}>
                    {displayLabel}
                </span>
                <span style={{ fontSize: 9, opacity: 0.6, marginLeft: 2 }}>{open ? "▴" : "▾"}</span>
            </button>

            {open && (
                <div
                    ref={popRef}
                    style={{
                        position: "fixed",
                        zIndex: 9999,
                        background: "#111827",
                        borderRadius: "10px",
                        padding: "12px",
                        width: "360px",
                        boxShadow: "0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.07)",
                    }}
                >
                    {/* Header row */}
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
                        <input
                            ref={searchRef}
                            type="text"
                            placeholder={`Search ${allIcons.length} icons…`}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                                flex: 1,
                                borderRadius: "6px",
                                border: "1.5px solid #374151",
                                padding: "6px 10px",
                                fontSize: "13px",
                                background: "#1f2937",
                                color: "#f9fafb",
                                outline: "none",
                            }}
                        />
                        {onColorChange && (
                            <div
                                title="Icon color"
                                style={{
                                    position: "relative",
                                    width: 34,
                                    height: 34,
                                    borderRadius: 6,
                                    border: "1.5px solid #374151",
                                    background: "#1f2937",
                                    overflow: "hidden",
                                    flexShrink: 0,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <div style={{
                                    width: 18, height: 18, borderRadius: "50%",
                                    background: color, border: "2px solid rgba(255,255,255,0.2)",
                                    pointerEvents: "none",
                                }} />
                                <input
                                    type="color"
                                    value={color}
                                    onChange={(e) => onColorChange(e.target.value)}
                                    style={{
                                        position: "absolute", inset: 0,
                                        width: "100%", height: "100%",
                                        opacity: 0, cursor: "pointer",
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Status bar */}
                    <div style={{
                        height: 18,
                        marginBottom: "6px",
                        fontSize: "11px",
                        color: "#6b7280",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingInline: 2,
                    }}>
                        <span>{tooltip ? <span style={{ color: "#9ca3af", fontWeight: 500 }}>{tooltip}</span> : `${filtered.length} icons`}</span>
                        {onColorChange && (
                            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <span style={{
                                    display: "inline-block", width: 10, height: 10,
                                    borderRadius: "50%", background: color,
                                    border: "1px solid rgba(255,255,255,0.2)",
                                }} />
                                <span>{color}</span>
                            </span>
                        )}
                    </div>

                    {/* Icon grid */}
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(9, 1fr)",
                        gap: "3px",
                        maxHeight: "280px",
                        overflowY: "auto",
                        paddingRight: 2,
                    }}>
                        {filtered.map((opt) => {
                            const Icon = ICON_MAP[opt.value];
                            if (!Icon) return null;
                            const isSelected = opt.value === value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    title={opt.label}
                                    onMouseEnter={() => setTooltip(opt.label)}
                                    onMouseLeave={() => setTooltip(null)}
                                    onClick={() => { onChange(opt.value); setOpen(false); }}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: "6px",
                                        borderRadius: "5px",
                                        cursor: "pointer",
                                        border: isSelected ? "1.5px solid #3b82f6" : "1.5px solid transparent",
                                        background: isSelected ? "#1e3a5f" : "transparent",
                                        transition: "background 0.1s",
                                    }}
                                    onMouseOver={(e) => {
                                        if (!isSelected) (e.currentTarget as HTMLElement).style.background = "#1f2937";
                                    }}
                                    onMouseOut={(e) => {
                                        if (!isSelected) (e.currentTarget as HTMLElement).style.background = "transparent";
                                    }}
                                >
                                    <Icon style={{ width: 18, height: 18, color: isSelected ? "#60a5fa" : "#9ca3af" }} />
                                </button>
                            );
                        })}
                    </div>

                    {filtered.length === 0 && (
                        <div style={{ color: "#6b7280", fontSize: "12px", textAlign: "center", padding: "20px 0 8px" }}>
                            No icons match "{search}"
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default IconPicker;
